import { TimeSlot } from '../types/booking';
import { CalendarDay } from '../components/core/Calendar/Calendar.types';
import { format, isSameDay } from 'date-fns';
import { getUserTimezone } from '../modules/timezone';
import { formatDateInTimezone } from '../utils/timezones';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import i18n from '../i18n';

/**
 * CalendarViewModel
 * 
 * Prepares and transforms data for the Calendar UI component.
 * This separates data transformation logic from UI components.
 */
export class CalendarViewModel {
  /**
   * Transforms raw time slots into a map of dates to their available slots
   * 
   * @param slots Array of time slots
   * @returns Map of date strings to arrays of time slots
   */
  public static transformSlotsToDateMap(slots: TimeSlot[]): Record<string, TimeSlot[]> {
    const dateMap: Record<string, TimeSlot[]> = {};
    
    slots.forEach(slot => {
      const dateKey = format(new Date(slot.start), 'yyyy-MM-dd');
      
      if (!dateMap[dateKey]) {
        dateMap[dateKey] = [];
      }
      
      dateMap[dateKey].push(slot);
    });
    
    return dateMap;
  }
  
  /**
   * Creates a formatted date string for display
   * 
   * @param date Date to format
   * @param timezone User's timezone
   * @returns Formatted date string
   */
  public static formatDateForDisplay(date: Date, timezone: string = getUserTimezone()): string {
    // Use formatDateInTimezone to ensure proper timezone handling
    return formatDateInTimezone(date, timezone, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }
  
  /**
   * Determines if a day has availability and adds this to its metadata
   * 
   * @param day Calendar day object
   * @param availableDates Map of dates to their available slots
   * @returns Enhanced calendar day with availability metadata
   */
  public static enhanceDayWithAvailability(
    day: CalendarDay,
    availableDates: Record<string, TimeSlot[]>
  ): CalendarDay {
    const dateKey = format(day.date, 'yyyy-MM-dd');
    const hasAvailability = availableDates[dateKey] && availableDates[dateKey].length > 0;
    
    return {
      ...day,
      metadata: {
        ...day.metadata,
        hasAvailability,
        availableSlotCount: hasAvailability ? availableDates[dateKey].length : 0
      }
    };
  }
  
  /**
   * Gets time slot for a specific date
   * 
   * @param date Date to get time slots for
   * @param availableSlots All available time slots
   * @returns Time slots for the specified date
   */
  public static getTimeSlotsForDate(date: Date, availableSlots: TimeSlot[]): TimeSlot[] {
    return availableSlots.filter(slot => {
      const slotDate = new Date(slot.start);
      return isSameDay(slotDate, date);
    });
  }
  
  /**
   * Sorts time slots chronologically
   * 
   * @param slots Time slots to sort
   * @returns Sorted time slots
   */
  public static sortTimeSlots(slots: TimeSlot[]): TimeSlot[] {
    return [...slots].sort((a, b) => {
      const dateA = new Date(a.start);
      const dateB = new Date(b.start);
      return dateA.getTime() - dateB.getTime();
    });
  }
  
  /**
   * Formats a time for display in the specified timezone
   *
   * @param date Date to format
   * @param timezone User's timezone
   * @returns Formatted time string
   */
  public static formatTimeInTimezone(date: Date, timezone: string = getUserTimezone()): string {
    // Ensure we're working with a Date object
    const dateObj = date instanceof Date ? date : new Date(date);

    const locale = i18n.language || 'en';
    const isJapanese = locale === 'ja';

    // Use formatDateInTimezone for consistent timezone handling
    return formatDateInTimezone(dateObj, timezone, {
      hour: 'numeric',
      minute: 'numeric',
      hour12: !isJapanese // 24-hour for Japanese, 12-hour for English
    });
  }
  
  /**
   * Formats a time slot for display
   * 
   * @param slot Time slot to format
   * @param timezone User's timezone
   * @returns Formatted time slot string
   */
  public static formatTimeSlot(slot: TimeSlot, timezone: string = getUserTimezone()): string {
    // Ensure we're working with Date objects
    const startDate = slot.start instanceof Date ? slot.start : new Date(slot.start);
    const endDate = slot.end instanceof Date ? slot.end : new Date(slot.end);
    
    // Use the timezone from the slot if available, otherwise use the provided timezone
    const effectiveTimezone = slot.timeZone || timezone;
    
    // Format the start and end times in the effective timezone
    const startTime = this.formatTimeInTimezone(startDate, effectiveTimezone);
    const endTime = this.formatTimeInTimezone(endDate, effectiveTimezone);
    
    return `${startTime} - ${endTime}`;
  }
  
  /**
   * Converts a date to the specified timezone
   * 
   * @param date Date to convert
   * @param timezone Target timezone
   * @returns Date in the target timezone
   */
  public static convertToTimezone(date: Date, timezone: string): Date {
    return utcToZonedTime(date, timezone);
  }
  
  /**
   * Converts a date from the specified timezone to UTC
   * 
   * @param date Date in the source timezone
   * @param timezone Source timezone
   * @returns Date in UTC
   */
  public static convertToUTC(date: Date, timezone: string): Date {
    return zonedTimeToUtc(date, timezone);
  }
}

export default CalendarViewModel; 